import { Tooltip } from '@insio/ui-toolkit';
import { TappeerEMark } from './TappeerBrandMark';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface ChatLauncherButtonProps {
  /** True when the assistant spoke while the chat was collapsed. */
  unread: boolean;
  onOpen: () => void;
}

/**
 * Round floating button standing in for the collapsed bubble chat; a click opens it.
 * Plain button on purpose — the toolkit Button has no round floating variant.
 */
export function ChatLauncherButton({ unread, onOpen }: ChatLauncherButtonProps) {
  const t = useFeatureTranslation('aiChat');

  return (
    // The tooltip wrapper is what floats — the bubble is positioned against it.
    // Fixed width: shrink-to-fit would size the bubble off the 56px button.
    <Tooltip
      content={
        <div className='w-full text-left'>
          <p className='text-sm font-semibold'>{t('title')}</p>
          <p className='mt-2xs'>{t('launcherHint')}</p>
        </div>
      }
      position='top right'
      className='fixed right-lg bottom-lg z-40'
      bubbleClassName='w-60 max-w-none px-md py-sm'
      // Centred over the button: half its width minus half the arrow.
      arrowClassName='right-6'
    >
      <button
        type='button'
        aria-label={t('openChat')}
        data-testid='ai-chat-launcher'
        onClick={onOpen}
        className='relative flex size-14 items-center justify-center rounded-full bg-secondary-base shadow-lg transition-shadow hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-base focus-visible:ring-offset-2'
      >
        <TappeerEMark size={30} chip={false} />
        {/* Dark, not pink: a pink dot would vanish on the pink button. */}
        {unread && (
          <span
            role='status'
            aria-label={t('unread')}
            className='ai-unread-dot absolute top-0 right-0 size-3 rounded-full bg-foreground-base ring-2 ring-white'
          />
        )}
      </button>
    </Tooltip>
  );
}
